/**
 * CONFIGURE A BLANK — `tasks/practice-documents.md` §0 decision 4, §3.1, §7, Phase 8.
 *
 * The second of the Documents tab's two dialogs, and the one the pencil in each panel header opens.
 * Its subject is a document that already exists: what happens to a patient who receives it, when
 * they are asked for it, and — separately — swapping the bytes underneath it.
 *
 * ── WHY IT IS NOT THE SAME DIALOG AS THE UPLOAD ──────────────────────────────────────────────
 *
 * See the header of `practice-document-upload-dialog.component.ts`. In short: different endpoints,
 * different bodies, and a `requiredInInvite` warning that only means anything about a document that
 * exists.
 *
 * ── WHY REPLACING THE FILE IS ITS OWN SECTION AND ITS OWN REQUEST ────────────────────────────
 *
 * `PUT /:id` is metadata and delivery flags; the file goes to `POST /:id/file`, which RE-INSPECTS.
 * Swapping a scan for a real AcroForm moves the document between §1.1 tiers and the `fillMode` on
 * the response is the new answer — so the row is replaced by what comes back rather than patched
 * locally, and a tier change is announced rather than left to be discovered by a patient.
 *
 * ── WHAT IT DOES NOT SET: §14.2 ──────────────────────────────────────────────────────────────
 *
 * `requiresResponse` is not a control here. A document's KIND — something the practice is waiting
 * on, or a sheet they hand out — is decided once, by which of the tab's two sections the add icon
 * was clicked in, and the upload dialog states the whole consequence at that moment. This dialog
 * says the kind read-only in its first line and then configures within it.
 *
 * A toggle here was wrong in both directions. An information sheet is information-only by
 * definition, so offering "the patient completes it and sends it back" against one offers to make
 * it something it is not; and by the same definition so is a document sitting under "documents
 * that need a response". What the toggle actually did was move a row between the two sections of
 * the tab behind this dialog, silently, from a switch three fields down a settings form.
 *
 * Everything downstream of the flag follows it out of this form for an information sheet: the two
 * link timings and the `requiredInInvite` hard block are not drawn, because no link is created for
 * one and nothing ever comes back from one to unblock an invite step.
 *
 * ── WHAT IT HANDS BACK ───────────────────────────────────────────────────────────────────────
 *
 * `changed` — whether anything was actually written. The tab re-reads its list on a `true`, because
 * the panel behind this dialog would otherwise still describe the previous file or the previous
 * flags. A cancel writes nothing and asks for no re-read.
 */

import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import { FileObject } from '../../../../core/interfaces/file-object';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { GroupService } from '../../../../core/services/group.service';
/*
 * §26. `Dr MedicAI Support` is a real GroupUser in every practice — `SupportService` invites it so
 * a support engineer can reach the file — but it is US, not a member of the practice's staff.
 * `patient-appointment-detail` already keeps it out of the Treating Physician picker for the same
 * reason; the id is imported from where it is defined rather than pasted a third time.
 */
import { SUPPORT_USER_ID } from '../../../../core/services/support.service';
import {
  PracticeDocumentDto, PracticeDocumentMetadata, PracticeDocumentService,
} from '../../../../core/services/practice-document.service';
import {
  NOT_YET_DELIVERED_NOTICE, REPLACE_FILE_NOTICE, REQUIRED_IN_INVITE_CONSEQUENCE,
  patientOutcomeSentence, patientOutcomeIcon, downloadOnlyReason, matchingCategories,
  replacedMessage, requiresResponse,
} from '../practice-document-copy';
import { PracticeDocumentPdfChoice } from '../practice-document-pdf-choice';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

export interface PracticeDocumentEditDialogData {
  document: PracticeDocumentDto;
  /** What the practice has already filed documents under. A SUGGESTION LIST, never a constraint. */
  categories?: string[];
}

/** `changed` is the only thing the tab needs: whether the list it is holding is now stale. */
export interface PracticeDocumentEditDialogResult {
  changed: boolean;
  document: PracticeDocumentDto;
}

@Component({
  selector: 'app-practice-document-edit-dialog',
  templateUrl: './practice-document-edit-dialog.component.html',
  styleUrls: ['./practice-document-edit-dialog.component.scss'],
})
export class PracticeDocumentEditDialogComponent extends BaseComponent {

  readonly requiredInInviteConsequence = REQUIRED_IN_INVITE_CONSEQUENCE;
  readonly replaceFileNotice = REPLACE_FILE_NOTICE;
  readonly notYetDeliveredNotice = NOT_YET_DELIVERED_NOTICE;

  /** The row as the server last described it. Replaced by every response, never patched. */
  document: PracticeDocumentDto;

  /**
   * The categories this practice has already used, handed over by the tab.
   *
   * Passed rather than fetched because the tab is holding the unfiltered document list they come
   * from — see its `documentCategories`. It includes THIS document's own category, which is right:
   * the panel is what the practice has filed things under, and a document being edited is one of
   * the things they filed.
   */
  readonly categories: string[];

  settingsForm: FormGroup;
  isSavingSettings = false;

  /** The replacement picker, its four checks, its refusal and the caps it quotes. */
  readonly choice = new PracticeDocumentPdfChoice();
  isReplacingFile = false;

  /** Set by anything that actually wrote. Read by the tab to decide whether to re-read its list. */
  private changed = false;

  /** A state unit (tasks/state-mode.md): documents are never sent to patients. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PracticeDocumentEditDialogData,
    private dialogRef: MatDialogRef<PracticeDocumentEditDialogComponent,
      PracticeDocumentEditDialogResult>,
    private formBuilder: FormBuilder,
    private practiceDocumentService: PracticeDocumentService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
    this.document = data.document;
    this.categories = data.categories || [];
    this.settingsForm = this.buildSettingsForm(this.document);
    // §26. Read from the service's held list rather than fetched: the group's users are already
    // loaded by the time a settings tab is open, and a dialog that fires its own request would
    // render an empty picker for as long as it took to answer.
    this.loadGroupMembers();
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * What will happen to a patient who receives this
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  get patientOutcomeSentence(): string {
    return patientOutcomeSentence(this.document);
  }

  get downloadOnlyReason(): string | null {
    return downloadOnlyReason(this.document);
  }

  get patientOutcomeIcon(): string {
    return patientOutcomeIcon(this.document);
  }

  /** True while the settings form has the hard block switched on, so its consequence is shown. */
  get requiredInInviteEnabled(): boolean {
    const control = this.settingsForm.get('requiredInInvite');
    return !!(control && control.value);
  }

  /**
   * §14.2 - read off the ROW, because it is no longer a control on this form.
   *
   * It used to be read off a `requiresResponse` toggle, which is what made it a value that could
   * change while the dialog was open. The kind of a document is now fixed at upload by which
   * section's add icon was clicked, so this is a fact about the row being edited, and what it
   * governs is which of this form's controls are drawn at all: an information sheet gets no link
   * timings and no hard block, because it has no link and nothing ever comes back from it.
   */
  get informationOnly(): boolean {
    return !requiresResponse(this.document);
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * The settings
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  private buildSettingsForm(document: PracticeDocumentDto): FormGroup {
    return this.formBuilder.group({
      name: [document.name, [Validators.required, Validators.maxLength(300)]],
      description: [document.description || ''],
      category: [document.category || ''],
      sendWithInvite: [document.sendWithInvite],
      requiredInInvite: [document.requiredInInvite],
      allowManualSend: [document.allowManualSend],
      // §14.2 has NO CONTROL HERE, and its absence is load-bearing - see `saveSettings`.
      sendWithConsentRequest: [!!document.sendWithConsentRequest],
      // §26 — who may act. `|| []` because the control binds a multi-select, and `null` there
      // renders as "nothing selected" but reads back as null rather than as an empty array.
      signOffUserIds: [document.signOffUserIds || []],
      validationUserIds: [document.validationUserIds || []],
      expiryDays: [document.expiryDays, [Validators.min(1), Validators.max(365)]],
      // null is meaningful (§3.1): "ask this patient once, ever" rather than "re-ask every time".
      active: [document.active],
    });
  }

  saveSettings(): void {
    if (this.isSavingSettings) return;

    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      this.showToastMessage('Check the highlighted fields.');
      return;
    }

    const value = this.settingsForm.value;

    /*
     * A typed metadata object, NOT the form value spread.
     *
     * `PracticeDocumentMetadata` has no `fillMode`, no `pageCount` and neither inspection boolean,
     * so a control added to this form later cannot become a field in the request body — and a PUT
     * can never claim the file is something the stored bytes say it is not.
     *
     * `requiresResponse` IS DELIBERATELY ABSENT, and absent rather than copied off the row. §14.2
     * is settled at upload and this dialog no longer offers to change it, so the safe body is the
     * one that does not mention it: every field here is optional on the controller side, which
     * applies only the flags a body actually carries (`practiceDocumentController.js:518-524`), so
     * an omitted flag is left exactly as stored.
     *
     * Sending `!!value.requiresResponse` off a form that no longer has that control would read
     * `undefined` and write FALSE — quietly converting every document that needs a response into
     * an information sheet on the next save, which is §14.2's silent failure exactly: the practice
     * waits for a return that is no longer being asked for.
     */
    const metadata: PracticeDocumentMetadata = {
      name: String(value.name || '').trim(),
      description: value.description ? String(value.description) : null,
      category: value.category ? String(value.category).trim() : null,
      sendWithInvite: !!value.sendWithInvite,
      requiredInInvite: !!value.requiredInInvite,
      allowManualSend: !!value.allowManualSend,
      sendWithConsentRequest: !!value.sendWithConsentRequest,
      expiryDays: this.numberOrNull(value.expiryDays),
      active: !!value.active,
      // §26. Named explicitly, like every field above — see this object's own note on why it is
      // built field by field. `[]` becomes null here as well as on the server: "nobody selected"
      // and "not configured" are one meaning and must have one representation.
      signOffUserIds: this.userIdsOrNull(value.signOffUserIds),
      validationUserIds: this.userIdsOrNull(value.validationUserIds),
    };

    this.isSavingSettings = true;
    this.practiceDocumentService.updateDocument(this.document.id, metadata)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSavingSettings = false;
          this.changed = true;
          this.document = response.data;
          this.settingsForm = this.buildSettingsForm(response.data);
          this.showToastMessage('Document settings saved.');
          this.close();
        },
        error: (error) => {
          this.isSavingSettings = false;
          this.showToastMessage(
            PracticeDocumentPdfChoice.messageFor(error, 'Those settings could not be saved.'));
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Replace the file
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * `app-file-upload` emits `FileObject[]`, NOT `File[]` — see the upload dialog's copy of this
   * method for the whole reasoning. The short version: `url` is a blob URL and the original `File`
   * is gone, `choose` and `replaceFile` both need a real one, so it is rebuilt from the blob with
   * the original name and bytes. All four of `choose`'s checks keep working unchanged, including
   * the `%PDF-` header check that the shared component's MIME-only filter cannot make.
   */
  async onFilesPicked(files: FileObject[]): Promise<void> {
    const picked = files && files.length ? files[0] : null;
    if (!picked) return;

    try {
      const blob = await (await fetch(picked.url)).blob();
      await this.selectFile(new File([blob], picked.name || 'document.pdf',
        { type: picked.type || 'application/pdf' }));
    } catch {
      this.choice.error = 'That file could not be read from this device. Try choosing it again.';
    } finally {
      URL.revokeObjectURL(picked.url);
    }
  }

  async selectFile(file: File): Promise<void> {
    await this.choice.choose(file);
  }

  clearReplacementFile(): void {
    this.choice.clear();
  }

  megabytes(bytes: number): string {
    return PracticeDocumentPdfChoice.megabytes(bytes);
  }

  /**
   * `POST /:id/file`. The server RE-INSPECTS, so the row is replaced by the response — including a
   * `fillMode` that may have changed. The dialog stays open on the new row so the practice can see
   * what their replacement did before they leave.
   */
  replaceFile(): void {
    if (this.isReplacingFile) return;

    if (!this.choice.file) {
      this.choice.error = 'Choose the replacement PDF first.';
      return;
    }

    const previousFillMode = this.document.fillMode;

    this.isReplacingFile = true;
    this.practiceDocumentService.replaceFile(this.document.id, this.choice.file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isReplacingFile = false;
          this.changed = true;
          this.document = response.data;
          this.settingsForm = this.buildSettingsForm(response.data);
          this.choice.clear();
          this.showToastMessage(replacedMessage(previousFillMode, response.data.fillMode));
        },
        error: (error) => {
          this.isReplacingFile = false;
          this.choice.applyServerCap(error);
          this.choice.error = PracticeDocumentPdfChoice.messageFor(
            error, 'The file could not be replaced.');
        },
      });
  }

  /* ══════════════════════════════════════════════════════════════════════════════════════════
   * Labels and closing
   * ════════════════════════════════════════════════════════════════════════════════════════ */

  /**
   * What to offer for what has been typed. A getter rather than a `valueChanges` stream: the list
   * is one practice's categories, the filter is a substring test, and a subscription here would
   * have to be rebuilt every time `buildSettingsForm` replaces the form — which a save and a file
   * replacement both do.
   */
  get filteredCategories(): string[] {
    const control = this.settingsForm.get('category');
    return matchingCategories(this.categories, control ? control.value : '');
  }

  get pageCountLabel(): string | null {
    return typeof this.document.pageCount !== 'number'
      ? null
      : `${this.document.pageCount} page(s)`;
  }

  get fileSizeLabel(): string | null {
    return typeof this.document.fileSizeBytes !== 'number'
      ? null
      : `${PracticeDocumentPdfChoice.megabytes(this.document.fileSizeBytes)} MB`;
  }

  close(): void {
    this.dialogRef.close({ changed: this.changed, document: this.document });
  }

  /* ════════════════════════════════════════════════════════════════════════════
   * §26 — WHO MAY ACT ON A RETURN OF THIS DOCUMENT
   *
   * Two optional lists. Empty means anyone in the practice, which is the default and what every
   * document has until somebody changes one here.
   *
   * ── THE ONE LINE THAT MATTERS: `userId`, NOT `id` ───────────────────────────────────────
   *
   * `getGroupUsers()` returns rows carrying BOTH `id` (the GroupUser row) and `userId` (the User).
   * The server compares these lists against `req.userId`, which is a `Users.id`. Binding `id`
   * would store values that can never match anybody — so every configured document would refuse
   * every user, the server's empty-intersection fallback would then let EVERYBODY through, and
   * the feature would look configured while enforcing nothing. Nothing errors anywhere along that
   * path, which is what makes it worth this many words.
   * ═══════════════════════════════════════════════════════════════════════════ */

  /** The practice's members, as `{ userId, label }`. Pending invites are NOT members yet. */
  groupMembers: { userId: string; label: string }[] = [];

  private loadGroupMembers(): void {
    const rows = this.groupService.groupUsers || [];
    this.groupMembers = (Array.isArray(rows) ? rows : [])
      // A pending invite has no `userId` because there is no user yet. Offering one would let a
      // practice name somebody who cannot sign in, which reads as a lock-out from the panel.
      .filter((row: any) => row && row.userId)
      // MedicAI Support is not the practice's clinician. Naming it here would put our own account
      // on the list of people who may sign a patient's document onto a clinical record.
      .filter((row: any) => row.userId !== SUPPORT_USER_ID)
      .map((row: any) => ({
        userId: String(row.userId),
        label: [row.title, row.name, row.surname].filter(Boolean).join(' ').trim()
          || row.email || 'Unknown',
      }));
  }

  /** True while a list names somebody who is no longer a member — the settings-side warning. */
  hasDepartedMembers(control: 'signOffUserIds' | 'validationUserIds'): boolean {
    const chosen: string[] = this.settingsForm.get(control)?.value || [];
    return chosen.some((id) => !this.groupMembers.some((m) => m.userId === id));
  }

  /** `[]` is not a stored value: it means the same thing as "not configured". */
  private userIdsOrNull(value: any): string[] | null {
    if (!Array.isArray(value)) return null;
    const ids = value.filter((id) => typeof id === 'string' && id.trim() !== '');
    return ids.length ? ids : null;
  }

  private numberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }
}
